/**
 * Shared design tokens + helpers for the public ticket pages
 * (events list, booking/checkout, success).
 *
 * Clean dark theme with a warm amber accent — "golden ticket" feel
 * without being loud.
 */

const API_BASE = process.env.REACT_APP_API_URL ?? '';

export const palette = {
  bg: '#0c0d10',
  surface: '#15171c',
  surfaceAlt: '#1b1e25',
  overlay: 'rgba(0,0,0,0.55)',
  border: 'rgba(255,255,255,0.08)',
  borderStrong: 'rgba(255,255,255,0.16)',
  accent: '#f0b545',
  accentSoft: 'rgba(240,181,69,0.12)',
  accentBorder: 'rgba(240,181,69,0.35)',
  accentHover: '#ffca5f',
  onAccent: '#1d1503',
  text: '#f4f5f7',
  textSecondary: '#a2a8b3',
  textMuted: '#6d7380',
  success: '#5dc46a',
  successSoft: 'rgba(93,196,106,0.12)',
  successBorder: 'rgba(93,196,106,0.35)',
  error: '#ef5350',
  warning: '#ffa726',
};

export const pageBackground = {
  minHeight: '100vh',
  backgroundColor: palette.bg,
  backgroundImage:
    'radial-gradient(ellipse 80% 50% at 50% -10%, rgba(240,181,69,0.07), transparent)',
};

export const cardSx = {
  backgroundColor: palette.surface,
  border: `1px solid ${palette.border}`,
  borderRadius: 3,
  overflow: 'hidden',
};

export const textFieldSx = {
  '& .MuiInputLabel-root': { color: palette.textSecondary },
  '& .MuiInputLabel-root.Mui-focused': { color: palette.accent },
  '& .MuiOutlinedInput-root': {
    color: palette.text,
    backgroundColor: palette.surfaceAlt,
    borderRadius: 2,
    '& fieldset': { borderColor: palette.border },
    '&:hover fieldset': { borderColor: palette.borderStrong },
    '&.Mui-focused fieldset': { borderColor: palette.accent },
  },
  '& .MuiFormHelperText-root': { color: palette.error },
};

export const primaryButtonSx = {
  backgroundColor: palette.accent,
  color: palette.onAccent,
  fontWeight: 700,
  textTransform: 'none',
  borderRadius: 2,
  boxShadow: 'none',
  '&:hover': { backgroundColor: palette.accentHover, boxShadow: 'none' },
  '&.Mui-disabled': { backgroundColor: '#2a2d35', color: palette.textMuted },
};

export const outlinedButtonSx = {
  color: palette.text,
  borderColor: palette.borderStrong,
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: 2,
  '&:hover': { borderColor: palette.accent, backgroundColor: palette.accentSoft },
};

export const assetUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${API_BASE}${path}`;
};

/** Square event poster. Crop is chosen in admin and reused on the ticket PDF. */
export const posterFrameSx = {
  position: 'relative',
  width: '100%',
  aspectRatio: '1 / 1',
  overflow: 'hidden',
};

export const posterImgSx = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  objectPosition: 'center',
  display: 'block',
};

export const formatPrice = (pence) => `£${((pence || 0) / 100).toFixed(2)}`;

export const fewLeftLabel = (item) => {
  if (item?.is_few_left_dummy) return 'Few Left';
  if (item?.tickets_remaining != null) return `Only ${item.tickets_remaining} left`;
  return 'Few Left';
};

export const formatEventDate = (dateStr, options) => {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...options,
  });
};

export const formatEventTime = (dateStr) => {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

export { API_BASE };
